import {Module} from "vuex";
import APIClient from "@/service/APIClient";
import ExportTaskResponseDTO from "@/models/ExportTaskResponseDTO";
import {ExportTaskCreateRequestDTO} from "@/service/exportTaskCreate";
import {ExportTaskStatus} from "@/models/enum/ExportTaskStatus";

const api = new APIClient();

const POLL_INTERVAL_MS = 2500;
const LIST_LIMIT = 20;

// Kept outside Vuex state on purpose: a setInterval handle is not meaningful JSON, and
// vuex-persistedstate serializes the whole tree to localStorage. A persisted handle would
// come back as a stale non-null number after a page reload (no interval actually running),
// which would make the startPolling guard below silently refuse to ever poll again.
let pollingTimer: ReturnType<typeof setInterval> | null = null;

const exportTasks: Module<any, any> = {
    namespaced: true,
    state: {
        list: [] as ExportTaskResponseDTO[],
        loaded: false,
    },
    actions: {
        async loadList({commit}) {
            const resp = await api.call.exportTasks.list({limit: LIST_LIMIT, offset: 0});
            commit('SET_LIST', resp.items);
        },
        async create({commit, dispatch}, payload: {sysname: string; requestDTO: ExportTaskCreateRequestDTO}) {
            const task = await api.call.exportTasks.create(payload.sysname, payload.requestDTO);
            commit('UPSERT_TASK', task);
            dispatch('startPolling');
            return task;
        },
        async cancel({commit}, id: string) {
            const task = await api.call.exportTasks.cancel(id);
            commit('UPSERT_TASK', task);
            return task;
        },
        startPolling({state, dispatch}) {
            if (pollingTimer !== null) {
                return;
            }

            pollingTimer = setInterval(async () => {
                try {
                    await dispatch('loadList');
                } catch (e) {
                    return;
                }

                if (!state.list.some((task: ExportTaskResponseDTO) => task.status === ExportTaskStatus.IN_PROGRESS)) {
                    dispatch('stopPolling');
                }
            }, POLL_INTERVAL_MS);
        },
        stopPolling() {
            if (pollingTimer !== null) {
                clearInterval(pollingTimer);
                pollingTimer = null;
            }
        },
    },
    mutations: {
        SET_LIST: (state, payload: ExportTaskResponseDTO[]) => {
            state.list = payload;
            state.loaded = true;
        },
        UPSERT_TASK: (state, task: ExportTaskResponseDTO) => {
            const index = state.list.findIndex((item: ExportTaskResponseDTO) => item.id === task.id);
            if (index === -1) {
                state.list = [task, ...state.list];
            } else {
                state.list = [
                    ...state.list.slice(0, index),
                    task,
                    ...state.list.slice(index + 1),
                ];
            }
        },
    },
    getters: {
        list: (state): ExportTaskResponseDTO[] => state.list,
        activeTask: (state): ExportTaskResponseDTO | null => {
            return state.list.find((task: ExportTaskResponseDTO) => task.status === ExportTaskStatus.IN_PROGRESS) ?? null;
        },
    }
}

export default exportTasks;
